import React from "react";
import {
  Button,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import reviewApi from "../api/reviewApi";
import ModulePageShell from "../components/common/ModulePageShell";
import OperationalPanel from "../components/common/OperationalPanel";
import StatusBadge from "../components/common/StatusBadge";
import { CompactEmptyState, PageSkeleton } from "../components/common/PageState";
import performanceQueryKeys from "../utils/queryKeys";
import { formatDate, personName, recordId, unwrapRows } from "../utils/normalize";

const TeamReviewsPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = React.useState({ search: "", status: "" });
  const params = { scope: "team", ...filters };
  const reviewsQuery = useQuery({
    queryKey: performanceQueryKeys.reviews.team(params),
    queryFn: () => reviewApi.listReviews(params),
  });
  const rows = unwrapRows(reviewsQuery.data);

  return (
    <ModulePageShell title="Team reviews" description="Track and complete manager assessment workflows." onRefresh={() => reviewsQuery.refetch()}>
      <OperationalPanel>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Input size="sm" placeholder="Search employee" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
          <Select size="sm" placeholder="Status" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>
            {["DRAFT", "IN_PROGRESS", "SUBMITTED", "FINALIZED", "ACKNOWLEDGED"].map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Select>
          <HStack justify="flex-end">
            <Button size="sm" variant="ghost" onClick={() => setFilters({ search: "", status: "" })}>Reset</Button>
          </HStack>
        </SimpleGrid>
      </OperationalPanel>
      <OperationalPanel>
        {reviewsQuery.isLoading ? (
          <PageSkeleton rows={6} />
        ) : rows.length === 0 ? (
          <CompactEmptyState title="No team reviews found." />
        ) : (
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Employee</Th>
                  <Th>Review</Th>
                  <Th>Current stage</Th>
                  <Th>Due date</Th>
                  <Th>Status</Th>
                  <Th isNumeric>Progress</Th>
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((review) => (
                  <Tr key={recordId(review)}>
                    <Td>{personName(review.employeeId || review.employee)}</Td>
                    <Td>
                      <Text fontWeight="semibold" fontSize="sm">{review.name || review.title || "Performance review"}</Text>
                    </Td>
                    <Td>{review.currentStage || "-"}</Td>
                    <Td>{formatDate(review.dueDate)}</Td>
                    <Td><StatusBadge value={review.status || review.finalStatus || "IN_PROGRESS"} /></Td>
                    <Td isNumeric>{review.progressPercentage ?? review.progress ?? 0}%</Td>
                    <Td><Button size="xs" variant="outline" onClick={() => navigate(`/performance/reviews/${recordId(review)}`)}>View</Button></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </OperationalPanel>
    </ModulePageShell>
  );
};

export default TeamReviewsPage;
